import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function Boarding() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    ownerName: '',
    email: '',
    phone: '',
    puppyName: '',
    breed: '',
    checkInDate: '',
    checkOutDate: '',
    specialRequirements: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    if (name === 'specialRequirements') {
      if (value.length <= 500) {
        setFormData(prev => ({ ...prev, [name]: value }));
        setCharCount(value.length);
      }
      return;
    }
    
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.ownerName || !formData.email || !formData.phone || !formData.puppyName || !formData.checkInDate || !formData.checkOutDate) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4j113cucjmktht58530', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          ownerName: '',
          email: '',
          phone: '',
          puppyName: '',
          breed: '',
          checkInDate: '',
          checkOutDate: '',
          specialRequirements: ''
        });
        setCharCount(0);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="boarding" className="py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t('boarding.title')} <span className="bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">{t('boarding.titleHighlight')}</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('boarding.subtitle')}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-start">
          <div 
            className="rounded-3xl overflow-hidden shadow-2xl h-[600px]"
            style={{
              backgroundImage: 'url(https://public.readdy.ai/ai/img_res/153ccf6793e1935778b6926cf8913f33.jpg)',
              backgroundSize: 'cover',
              backgroundPosition: 'center top'
            }}
          />

          <div className="bg-white rounded-3xl shadow-2xl p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">{t('boarding.formTitle')}</h3>
            
            <form id="boarding-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="ownerName" className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('boarding.ownerName')} <span className="text-pink-500">*</span>
                </label>
                <input
                  type="text"
                  id="ownerName"
                  name="ownerName"
                  value={formData.ownerName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-amber-400 focus:outline-none transition-colors text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('boarding.email')} <span className="text-pink-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-amber-400 focus:outline-none transition-colors text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('boarding.phone')} <span className="text-pink-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-amber-400 focus:outline-none transition-colors text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="puppyName" className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('boarding.puppyName')} <span className="text-pink-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="puppyName"
                    name="puppyName"
                    value={formData.puppyName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-amber-400 focus:outline-none transition-colors text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="breed" className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('boarding.breed')}
                  </label>
                  <select
                    id="breed"
                    name="breed"
                    value={formData.breed}
                    onChange={handleChange}
                    className="w-full px-4 py-3 pr-8 rounded-xl border-2 border-gray-200 focus:border-amber-400 focus:outline-none transition-colors text-sm cursor-pointer"
                  >
                    <option value="">{t('boarding.selectBreed')}</option>
                    <option value="poodle">{t('boarding.poodle')}</option>
                    <option value="maltipoo">{t('boarding.maltipoo')}</option>
                    <option value="other">{t('boarding.other')}</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="checkInDate" className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('boarding.checkInDate')} <span className="text-pink-500">*</span>
                  </label>
                  <input
                    type="date"
                    id="checkInDate"
                    name="checkInDate"
                    value={formData.checkInDate}
                    onChange={handleChange}
                    required
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-amber-400 focus:outline-none transition-colors text-sm cursor-pointer"
                  />
                </div>

                <div>
                  <label htmlFor="checkOutDate" className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('boarding.checkOutDate')} <span className="text-pink-500">*</span>
                  </label>
                  <input
                    type="date"
                    id="checkOutDate"
                    name="checkOutDate"
                    value={formData.checkOutDate}
                    onChange={handleChange}
                    required
                    min={formData.checkInDate || new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-amber-400 focus:outline-none transition-colors text-sm cursor-pointer"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="specialRequirements" className="block text-sm font-semibold text-gray-700 mb-2">
                  {t('boarding.specialRequirements')}
                </label>
                <textarea
                  id="specialRequirements"
                  name="specialRequirements"
                  value={formData.specialRequirements}
                  onChange={handleChange}
                  rows={4}
                  maxLength={500}
                  placeholder={t('boarding.specialRequirementsPlaceholder')}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-amber-400 focus:outline-none transition-colors resize-none text-sm"
                />
                <div className="text-right text-sm text-gray-500 mt-1">
                  {charCount}/500 {t('contact.charactersLimit')}
                </div>
              </div>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border-2 border-green-200 text-green-700 px-4 py-3 rounded-xl">
                  {t('boarding.successMessage')}
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-xl">
                  {t('boarding.errorMessage')}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-amber-500 to-amber-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:from-amber-600 hover:to-amber-700 transition-all shadow-xl hover:shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? t('boarding.submitting') : t('boarding.submitBooking')}
              </button>
            </form>
          </div>
        </div>

        <div className="mt-16 grid md:grid-cols-3 gap-8">
          <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-hotel-bed-line text-3xl text-white"></i>
            </div>
            <h4 className="text-xl font-bold text-gray-900 mb-2">{t('boarding.feature1Title')}</h4>
            <p className="text-gray-600">{t('boarding.feature1Desc')}</p>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-heart-pulse-line text-3xl text-white"></i>
            </div>
            <h4 className="text-xl font-bold text-gray-900 mb-2">{t('boarding.feature2Title')}</h4>
            <p className="text-gray-600">{t('boarding.feature2Desc')}</p>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-camera-line text-3xl text-white"></i>
            </div>
            <h4 className="text-xl font-bold text-gray-900 mb-2">{t('boarding.feature3Title')}</h4>
            <p className="text-gray-600">{t('boarding.feature3Desc')}</p>
          </div>
        </div>
      </div>
    </section>
  );
}
